import { summarizeBite } from "@/lib/bite-summary";
import { parseDateFound, type BiteEntry } from "@/lib/bite-log";
import { getIdProfile } from "@/lib/tick-id";
import { track } from "@/lib/analytics";
import { CARD_SURFACE } from "@/components/page-shell";
import { cn } from "@/lib/utils";
import { Printer } from "lucide-react";

function longDate(date: Date): string {
  return date.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

/**
 * Print only the summary: mark the document for the duration of the print dialog, and
 * let the print stylesheet (src/styles.css) hide everything else in the page body.
 */
function printSummary() {
  if (typeof window === "undefined") return;
  const root = document.documentElement;
  track("print_action_plan", { page: "tick-bite-summary" });
  root.dataset.printScope = "bite-summary";
  const clear = () => {
    delete root.dataset.printScope;
    window.removeEventListener("afterprint", clear);
  };
  window.addEventListener("afterprint", clear);
  window.print();
  // Some browsers never fire afterprint; print() blocks until the dialog closes anyway.
  window.setTimeout(clear, 0);
}

/**
 * "This bite", on one card: when it happened, how long to keep watching, and what to
 * watch for. It sits in the right-hand column of the bite tool on a wide screen, stays in
 * view while the steps scroll past, and prints on its own as a page for a clinician.
 *
 * Everything in it is computed by summarizeBite() from the sourced onset table, and it
 * renders only after the log has been read on this device, so the server never guesses
 * a date.
 */
export function BiteSummaryPanel({
  entry,
  ready,
  onLog,
}: {
  entry?: BiteEntry;
  ready: boolean;
  onLog: () => void;
}) {
  const today = new Date();
  const bitten = entry ? (parseDateFound(entry.dateFound) ?? undefined) : undefined;
  const profile = entry?.speciesSlug ? getIdProfile(entry.speciesSlug) : undefined;
  const slugs = profile && profile.diseaseSlugs.length > 0 ? profile.diseaseSlugs : undefined;
  const s = summarizeBite({ bitten, today, slugs });

  return (
    <section
      aria-labelledby="bite-summary-heading"
      data-print-region="bite-summary"
      className={cn(CARD_SURFACE, "border-t-[3px] border-t-foreground px-5 pb-5 pt-4")}
    >
      <h2 id="bite-summary-heading" className="label-caps text-muted-foreground">
        {bitten ? "This bite" : "After any tick bite"}
      </h2>

      {!ready ? (
        <p className="mt-3 text-sm text-muted-foreground">Reading your bite log on this device…</p>
      ) : (
        <>
          {bitten ? (
            <dl className="mt-3 divide-y divide-border border-y border-border">
              <div className="py-2.5">
                <dt className="text-xs text-muted-foreground">Bitten</dt>
                <dd className="font-display text-xl leading-tight text-foreground">
                  {longDate(bitten)}
                </dd>
                {s.dayNumber !== undefined && s.dayNumber >= 0 && (
                  <dd className="tabular text-sm text-muted-foreground">
                    {s.dayNumber === 0 ? "Today is day 0" : `Today is day ${s.dayNumber}`}
                  </dd>
                )}
              </div>
              {s.watchUntil && s.watchDays !== undefined && (
                <div className="py-2.5">
                  <dt className="text-xs text-muted-foreground">Watch until</dt>
                  <dd className="font-display text-xl leading-tight text-foreground">
                    {longDate(s.watchUntil)}
                  </dd>
                  <dd className="tabular text-sm text-muted-foreground">
                    {s.daysLeft === 0
                      ? `The ${s.watchDays}-day window has passed`
                      : `${s.watchDays} days in all, ${s.daysLeft} to go`}
                  </dd>
                </div>
              )}
              {profile && (
                <div className="py-2.5">
                  <dt className="text-xs text-muted-foreground">Tick</dt>
                  <dd className="text-[0.9375rem] font-medium text-foreground">
                    {profile.commonName}
                  </dd>
                </div>
              )}
            </dl>
          ) : (
            <div className="mt-3 border-y border-border py-3">
              <p className="text-[0.9375rem] leading-relaxed text-foreground">
                Keep watch for <strong className="font-semibold">{s.watchDays} days</strong> after
                the bite. That is the longest published window for any illness on this site.
              </p>
              <button
                type="button"
                onClick={onLog}
                className="link mt-2 min-h-11 text-sm font-semibold"
              >
                Write down the date to count it
              </button>
            </div>
          )}

          {s.openNow.length > 0 && (
            <p className="mt-3 text-sm leading-relaxed text-foreground">
              <span className="font-semibold">Open today:</span> {s.openNow.join(", ")}.
            </p>
          )}

          <h3 className="mt-4 text-sm font-semibold text-foreground">What to watch for</h3>
          <ul className="mt-1.5 space-y-1 text-sm leading-snug text-foreground">
            {s.signs.map((sign) => (
              <li key={sign} className="flex gap-2">
                <span aria-hidden className="shrink-0 text-rust-ink">
                  •
                </span>
                {sign}
              </li>
            ))}
          </ul>

          {s.alphaGal && (
            <p className="mt-3 text-sm leading-snug text-foreground">
              <span className="font-semibold">Alpha-gal has no countdown.</span> Watch for hives,
              stomach upset or swelling 2 to 6 hours after red meat or dairy, at any time.
            </p>
          )}

          <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
            {s.openEnded
              ? "Some of these illnesses have no firm window, so the end date is not an all-clear. "
              : "The end date is when the published windows close, not an all-clear. "}
            If you feel unwell, tell a clinician you were bitten, and when.
          </p>

          <button
            type="button"
            onClick={printSummary}
            className="no-print mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-sm border border-foreground/30 px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-foreground"
          >
            <Printer aria-hidden className="h-4 w-4" />
            Print this summary
          </button>
        </>
      )}
    </section>
  );
}
